// noinspection DuplicatedCode
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";

import { Colors } from "../../../assets/Colors";
import { fetchGet, fetchPost, fetchPostMultipartSeaweed } from "../../../fetchers";
import { v4 as uuidv4 } from "uuid";
import ModalBayar from "../../../components/form/ModalBayar";
import { useMutation } from "@tanstack/react-query";
import { Card, Button } from "@material-tailwind/react";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { useForm } from "../../../components/isRequired";
import { SearchBarSmallV2 } from "../../../components/form/SearchBar";
import Skeltons from "../../../components/loading/Skeltons";
import { downloadExcel } from "react-export-table-to-excel";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "react-toastify";
import { useUserBisnisUnit } from "../../../hooks/useUserBisnisUnit";

const PAGE_SIZE = 10;

// User SO hanya boleh melihat data unitnya sendiri — filter Bisnis Unit dikunci
// ke SO sehingga data HO tak pernah diminta ke API. User HO tetap bebas memilih.
const SO_UNIT = "U0002";

// Sentinel gabungan semua unit. Backend memakainya di WHERE
// ("BisnisUnitID" = $1 OR $1 = 'ALL'), jadi nilainya harus sama persis.
// Hanya ditawarkan ke user HO — user SO tetap terkunci ke unitnya sendiri.
const ALL_BU = "ALL";

const NAMA_BULAN = [
  "", "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

// ── Format helpers (gaya KasBankPage) ────────────────────────
const fmtRp = (v) =>
  Math.round(parseFloat(v || 0)).toLocaleString("id-ID", {
    maximumFractionDigits: 0,
  });
const fmtTgl = (v) => (v ? new Date(v).toLocaleDateString("id-ID") : "-");

// ── Status ───────────────────────────────────────────────────
// Backend menulis Status=2 (lalu memetakannya ke "Revisi") untuk baris DITOLAK,
// jadi kunci "2"/"revisi" wajib -> Ditolak supaya tampilan = data sebenarnya.
const STATUS_MAP = {
  1: { value: "Approved", isApproved: true, isRejected: false },
  2: { value: "Ditolak", isApproved: false, isRejected: true },
  3: { value: "Rejected", isApproved: false, isRejected: true },
  0: { value: "Pending", isApproved: false, isRejected: false },
  approved: { value: "Approved", isApproved: true, isRejected: false },
  approve: { value: "Approved", isApproved: true, isRejected: false },
  pending: { value: "Pending", isApproved: false, isRejected: false },
  rejected: { value: "Rejected", isApproved: false, isRejected: true },
  disetujui: { value: "Approved", isApproved: true, isRejected: false },
  ditolak: { value: "Rejected", isApproved: false, isRejected: true },
  revisi: { value: "Ditolak", isApproved: false, isRejected: true },
  "menunggu approval": { value: "Pending", isApproved: false, isRejected: false },
  "menunggu dikirim": { value: "Menunggu Dikirim", isApproved: false, isRejected: false },
};
const mapStatus = (status) => {
  if (!status) return { value: "Pending", isApproved: false, isRejected: false };
  return (
    STATUS_MAP[status.toString().toLowerCase()] || {
      value: status,
      isApproved: false,
      isRejected: false,
    }
  );
};

// Status yang ditampilkan per baris. "Close" MENGGANTIKAN label Approved untuk uang
// muka yang siklusnya tuntas (sudah cair + sudah dipotongkan di PembayaranAP yang
// disetujui). IsClose dihitung backend sebagai kolom turunan — kolom Status di DB
// tidak ikut berubah, jadi baris ini tetap Approved bagi alur lain.
const rowStatus = (row) =>
  row?.IsClose
    ? { value: "Close", isApproved: false, isRejected: false, isClose: true }
    : mapStatus(row?.Status);

// ── Export ───────────────────────────────────────────────────
// Kolom "Bisnis Unit" hanya ikut saat filter unit = ALL; kalau laporannya sudah
// dibatasi ke satu unit, kolom itu cuma mengulang nilai yang sama di tiap baris.
const exportHeader = (withUnit) => [
  "Kas Bank ID",
  ...(withUnit ? ["Bisnis Unit"] : []),
  "Bagian", "Kepada", "Jenis Transaksi", "No Cek / Biro",
  "Tanggal", "Keterangan", "Jurnal", "Nominal", "Status", "Tanggal Close",
];
const buildExportBody = (rows, withUnit) => {
  const header = exportHeader(withUnit);
  let total = 0;
  const body = rows.map((r) => {
    total += parseFloat(r.Nominal) || 0;
    return [
      r.CashJournalID || "",
      ...(withUnit ? [r.NamaBisnisUnit || r.BisnisUnitID || ""] : []),
      r.BagianID || "",
      r.DariKepada || "",
      r.JenisTransaksi || "",
      r.NoCek && r.NoCek !== "0" ? r.NoCek : "-",
      fmtTgl(r.Tanggal),
      r.Keterangan || "",
      r.JenisCashJournal || "",
      fmtRp(r.Nominal),
      rowStatus(r).value,
      r.TanggalClose ? fmtTgl(r.TanggalClose) : "",
    ];
  });
  // Baris TOTAL dirakit dari posisi header, bukan dari jumlah "" yang dihitung
  // manual — jumlah kolom berubah tergantung withUnit.
  const totalRow = header.map(() => "");
  totalRow[header.indexOf("Jurnal")] = "TOTAL";
  totalRow[header.indexOf("Nominal")] = fmtRp(total);
  body.push(totalRow);
  return body;
};

// ── Peta aktor approval ──────────────────────────────────────
// Backend (GetMonitoringPembayaranNonAP) mengirim ApprovalList: urutan approver
// grup "PermohonanPembayaranNonPO" beserta posisi tiap orang. Penandanya sengaja
// sama dengan monitoring MSR supaya kolom approval terbaca seragam di seluruh
// aplikasi: ✅ sudah setuju, ⏳ dokumen ada di tangannya, ⚪ belum giliran,
// "❌ Ditolak" pelaku penolakan.
const aktorClass = (status = "") => {
  if (status.includes("Ditolak")) return "text-red-600";
  if (status.includes("✅")) return "text-green-600";
  if (status.includes("⏳")) return "text-yellow-600 font-medium";
  return "text-gray-400";
};

// Warna teks status di tabel (gaya KasBankPage), tapi memakai peta status report.
const statusClass = (s) =>
  s.isClose
    ? "text-blue-600 font-semibold"
    : s.isApproved
    ? "text-green-600 font-medium"
    : s.isRejected
    ? "text-red-600 font-medium"
    : s.value === "Pending"
    ? "text-yellow-600 font-medium"
    : "text-gray-600";

// Label status PERSIS seperti yang tertulis di kolom Status tabel — "Terbayar"
// bukan bagian dari rowStatus() (itu tetap "Approved" + TanggalBayar terpisah),
// jadi filter status harus memakai label tampilan ini, bukan rowStatus().value,
// supaya "Filter: Terbayar" benar-benar cocok dgn apa yang terlihat pengguna.
const displayStatusLabel = (row) => {
  const st = rowStatus(row);
  if (st.isApproved && row.TanggalBayar) return "Terbayar";
  return st.value;
};

const MonitoringNonPO = () => {
  const navigate = useNavigate();

  const now = new Date();
  const initialState = { BisnisUnitID: "U0002", Bulan: new Date() };
  const { values, errors, touched, customHandler } = useForm(initialState, []);

  // Peran unit user login. Selama BU belum ter-resolve (sesi lama → fetch by Nik),
  // filter dikunci dulu; dibuka kembali kalau ternyata user bukan SO.
  const { bisnisUnitID: userBU, resolved: buResolved } = useUserBisnisUnit();
  const isSO = userBU === SO_UNIT;
  const lockBU = isSO || !buResolved;

  // Filter yang sudah DITERAPKAN (via "Buka Data"). Data report per-periode,
  // jadi Bulan selalu ada.
  const [applied, setApplied] = useState({
    bu: "U0002",
    bulan: (now.getMonth() + 1).toString(),
    tahun: now.getFullYear().toString(),
  });
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  // Filter Jenis Transaksi. "" = semua. Bekerja client-side atas data periode yang
  // sudah dimuat, sama seperti kotak pencarian — jadi TIDAK perlu tekan "Buka".
  const [jenisFilter, setJenisFilter] = useState("");
  // Filter Status — pola identik dgn Jenis Transaksi (client-side, tanpa "Buka").
  const [statusFilter, setStatusFilter] = useState("");
  // Baris yang sedang ditampilkan alasan penolakannya; null = popup tertutup.
  const [rejectInfo, setRejectInfo] = useState(null);

  // Ambil SEMUA data periode terpilih sekali (chunked), lalu semua filter /
  // pencarian / pagination dilakukan client-side — persis pola KasBankPage.
  const mutationData = useMutation(async ({ bu, bulan, tahun }) => {
    const endpoint = `Accounting/Mon/CashJournal/${bu}/NonPO/${bulan}/${tahun}`;
    const first = await fetchGet(`${endpoint}?page=1&limit=1`);
    const total = first?.count || 0;
    if (!total) return [];
    const CHUNK = 1000;
    const pages = Math.ceil(total / CHUNK);
    let all = [];
    for (let i = 0; i < pages; i++) {
      const r = await fetchGet(`${endpoint}?page=${i + 1}&limit=${CHUNK}`);
      all = all.concat(r?.data || []);
    }
    return all;
  });

  const { mutate } = mutationData;
  useEffect(() => {
    mutate(applied);
    // Muat sekali saat halaman dibuka dgn periode default.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Modal "Bayar" — sejak jurnal terbit saat pembayaran (bukan saat approval),
  // tombol inilah yang benar-benar menerbitkan jurnal.
  const [bayarModal, setBayarModal] = useState({ show: false, id: "", nominal: "" });
  const [bayarBusy, setBayarBusy] = useState(false);

  // Buka bukti bayar dari layanan upload (PDF/gambar di tab baru).
  const bukaBukti = useCallback(async (nama) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_UPLOAD_URL}file/${encodeURIComponent(nama)}`);
      if (!res.ok) throw new Error(String(res.status));
      const url = window.URL.createObjectURL(await res.blob());
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (_) {
      toast.error("Gagal membuka bukti bayar. File mungkin belum diupload ke server.", { position: "bottom-right" });
    }
  }, []);

  const handleSimpanBayar = useCallback(
    async (tanggal, buktiFile) => {
      setBayarBusy(true);
      try {
        // Bukti bayar (opsional): unggah dulu lewat layanan upload generik; hanya
        // NAMA file (ber-UUID) yang disimpan. Gagal unggah = batal simpan, agar
        // pembayaran tak tercatat dengan bukti yang tak pernah sampai ke server.
        let buktiBayar = "";
        if (buktiFile) {
          buktiBayar = `${uuidv4()}-${buktiFile.name.replace(/\s+/g, "_")}`;
          const fd = new FormData();
          fd.append("file", buktiFile, buktiBayar);
          try {
            await fetchPostMultipartSeaweed(fd);
          } catch (_) {
            toast.error("Upload bukti bayar gagal — pembayaran belum disimpan.", { position: "bottom-right" });
            return;
          }
        }
        // fetchPost memunculkan toast sukses/gagal sendiri dan MELEMPAR error
        // saat gagal — jadi tak perlu toast tambahan di sini.
        await fetchPost("Accounting/CashJournal/bayar", {
          ID: bayarModal.id,
          TanggalBayar: tanggal,
          BuktiBayar: buktiBayar,
        });
        setBayarModal({ show: false, id: "", nominal: "" });
        mutate(applied);
      } catch (_) {
        // Modal dibiarkan terbuka saat gagal: penyebab tersering adalah detail
        // lawan kosong / tidak seimbang, dan pengguna perlu konteksnya.
      } finally {
        setBayarBusy(false);
      }
    },
    [bayarModal.id, mutate, applied]
  );

  const allRows = useMemo(
    () => (Array.isArray(mutationData.data) ? mutationData.data : []),
    [mutationData.data],
  );

  const periodeLabel = `${NAMA_BULAN[parseInt(applied.bulan, 10)] || applied.bulan} ${applied.tahun}`;
  // Mode gabungan: unit tiap baris berbeda, jadi kolom Bisnis Unit ikut ditampilkan
  // dan label "BU: ALL" diganti sesuatu yang terbaca.
  const isAllBU = applied.bu === ALL_BU;
  const buLabel = isAllBU ? "Semua Unit" : applied.bu;

  // Pilihan Jenis Transaksi diturunkan dari data periode yang sedang dimuat,
  // bukan daftar tetap: nilainya kolom bebas di "CashJournal", jadi daftar keras
  // akan diam-diam menyembunyikan jenis baru begitu ada yang ditambahkan.
  const jenisOptions = useMemo(() => {
    const hitung = new Map();
    allRows.forEach((r) => {
      const j = (r.JenisTransaksi || "").trim();
      if (j) hitung.set(j, (hitung.get(j) || 0) + 1);
    });
    return [...hitung.entries()].sort((a, b) => b[1] - a[1]);
  }, [allRows]);

  // Pilihan Status, pola identik dgn jenisOptions — diturunkan dari data periode
  // yang sedang dimuat (label PERSIS kolom Status, termasuk "Terbayar").
  const statusOptions = useMemo(() => {
    const hitung = new Map();
    allRows.forEach((r) => {
      const s = displayStatusLabel(r);
      hitung.set(s, (hitung.get(s) || 0) + 1);
    });
    return [...hitung.entries()].sort((a, b) => b[1] - a[1]);
  }, [allRows]);

  // Ganti periode/unit bisa membuat jenis yang sedang dipilih tak ada lagi. Kalau
  // dibiarkan, dropdown menunjuk nilai yang tak ada di daftarnya dan tabel tampak
  // kosong tanpa sebab. Jenis yang masih ada sengaja DIPERTAHANKAN — pengguna yang
  // menelusuri "Penerimaan" bulan demi bulan tak perlu memilih ulang tiap kali.
  useEffect(() => {
    if (mutationData.isLoading || !allRows.length || !jenisFilter) return;
    if (!jenisOptions.some(([j]) => j === jenisFilter)) setJenisFilter("");
  }, [jenisOptions, jenisFilter, allRows.length, mutationData.isLoading]);

  // Sama untuk Status — reset kalau nilai yang sedang dipilih tak ada lagi di
  // periode/unit baru.
  useEffect(() => {
    if (mutationData.isLoading || !allRows.length || !statusFilter) return;
    if (!statusOptions.some(([s]) => s === statusFilter)) setStatusFilter("");
  }, [statusOptions, statusFilter, allRows.length, mutationData.isLoading]);

  // ── Filter (jenis transaksi + search) + sort client-side ───
  const filtered = useMemo(() => {
    let rows = [...allRows];
    if (jenisFilter) {
      rows = rows.filter(
        (r) => (r.JenisTransaksi || "").trim() === jenisFilter,
      );
    }
    if (statusFilter) {
      rows = rows.filter((r) => displayStatusLabel(r) === statusFilter);
    }
    const kw = searchQuery.trim().toLowerCase();
    if (kw) {
      rows = rows.filter((r) =>
        [
          r.CashJournalID,
          r.NamaBisnisUnit,
          r.BagianID,
          r.DariKepada,
          r.JenisTransaksi,
          r.Keterangan,
          r.JenisCashJournal,
          rowStatus(r).value,
        ].some((v) => v && v.toString().toLowerCase().includes(kw)),
      );
    }
    rows.sort((a, b) => {
      const ta = a.Tanggal ? new Date(a.Tanggal).getTime() : 0;
      const tb = b.Tanggal ? new Date(b.Tanggal).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return (b.CashJournalID || "").localeCompare(a.CashJournalID || "");
    });
    return rows;
  }, [allRows, searchQuery, jenisFilter, statusFilter]);

  const grandTotal = useMemo(
    () => filtered.reduce((s, r) => s + (parseFloat(r.Nominal) || 0), 0),
    [filtered],
  );

  // ── Pagination client-side ─────────────────────────────────
  const totalPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPage);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, applied, jenisFilter, statusFilter]);

  // User SO: nilai filter dipaksa balik ke SO — dropdown memang terkunci, ini
  // jaring pengaman kalau nilai sempat berubah sebelum BU user ter-resolve.
  useEffect(() => {
    if (isSO && values.BisnisUnitID !== SO_UNIT) {
      customHandler(SO_UNIT, "BisnisUnitID");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSO, values.BisnisUnitID]);

  // ── Handlers ───────────────────────────────────────────────
  const handleSearchChange = useCallback((e) => {
    const val = typeof e === "string" ? e : e?.target?.value || "";
    setSearchInput(val);
    setSearchQuery(val);
  }, []);

  const filterApi = () => {
    const d = new Date(values.Bulan);
    const next = {
      // User SO tak bisa meminta unit lain, apa pun isi state filter.
      bu: isSO ? SO_UNIT : values.BisnisUnitID || "U0002",
      bulan: (d.getMonth() + 1).toString(),
      tahun: d.getFullYear().toString(),
    };
    setApplied(next);
    setSearchInput("");
    setSearchQuery("");
    mutate(next);
    toast.success(
      `Menampilkan data ${NAMA_BULAN[d.getMonth() + 1]} ${d.getFullYear()}`,
      { position: "top-center", autoClose: 1000 },
    );
  };

  const handleRefresh = () => {
    mutate(applied);
    toast.info("Memuat ulang data...", {
      position: "top-center",
      autoClose: 1000,
    });
  };

  const handleViewDetail = (cashJournalId) => {
    if (!cashJournalId) return;
    navigate(`/finance/detail/form-kas-bank/${cashJournalId.toString().replace(/\//g, "-")}`);
  };

  const handleViewBlanko = (cashJournalId) => {
    if (!cashJournalId) {
      toast.error("ID Cash Journal tidak ditemukan");
      return;
    }
    navigate(`/finance/report/blanco/pembayaran-non-po/${cashJournalId.toString().replace(/\//g, "-")}`);
  };

  // ── Export (seluruh data terfilter) ────────────────────────
  // Nama berkas ikut menyebut jenis yang sedang disaring, supaya unduhan
  // "Pengeluaran saja" tidak tertukar dengan unduhan lengkap periode yang sama.
  const exportFileName = `Monitoring_NonPO_${applied.bu}_${applied.bulan}_${applied.tahun}${
    jenisFilter ? `_${jenisFilter.replace(/\s+/g, "-")}` : ""
  }${statusFilter ? `_${statusFilter.replace(/\s+/g, "-")}` : ""}`;

  const handleDownloadExcel = () => {
    if (filtered.length === 0) {
      toast.warn("Tidak ada data untuk di-export", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    downloadExcel({
      fileName: exportFileName,
      sheet: "Monitoring Non PO",
      tablePayload: {
        header: exportHeader(isAllBU),
        body: buildExportBody(filtered, isAllBU),
      },
    });
  };

  const handleDownloadPDF = () => {
    if (filtered.length === 0) {
      toast.warn("Tidak ada data untuk di-export", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("MONITORING PEMBAYARAN NON PO", 14, 15);
    doc.setFontSize(10);
    doc.text(
      `Periode: ${periodeLabel} | BU: ${buLabel}${
        jenisFilter ? ` | Jenis Transaksi: ${jenisFilter}` : ""
      }${statusFilter ? ` | Status: ${statusFilter}` : ""}`,
      14,
      22,
    );
    const header = exportHeader(isAllBU);
    const body = buildExportBody(filtered, isAllBU);
    autoTable(doc, {
      head: [header],
      body,
      startY: 28,
      styles: { fontSize: 6.5, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold" },
      didParseCell: (data) => {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [225, 232, 240];
        }
        // Kolom Nominal — indeksnya bergeser tergantung ada/tidaknya kolom unit.
        if (data.column.index === header.indexOf("Nominal")) {
          data.cell.styles.halign = "right";
        }
      },
    });
    doc.save(`${exportFileName}.pdf`);
  };

  const colHeader = [
    "No", "Kas Bank ID",
    ...(isAllBU ? ["Bisnis Unit"] : []),
    "Bagian", "Kepada", "Jenis Transaksi", "No Cek / Biro",
    "Tanggal", "Keterangan", "Jurnal", "Nominal", "Status", "Tanggal Close", "Aksi",
  ];

  return (
    <div className="md:ml-64">
      <ModalBayar
        show={bayarModal.show}
        dokumenId={bayarModal.id}
        nominal={bayarModal.nominal}
        tanggalDokumen={bayarModal.tanggalDokumen}
        busy={bayarBusy}
        uploadBukti
        onSimpan={handleSimpanBayar}
        onBatal={() => setBayarModal({ show: false, id: "", nominal: "" })}
      />
      <div
        style={{ background: Colors.primary }}
        className="pt-14 pb-16 px-3 md:px-8 h-auto"
      >
        <div className="container mx-auto max-w-full" />
      </div>
      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 px-4">
            <Card>
              {/* ── Filter row ── */}
              {/* Grid (bukan flex-wrap) supaya 5 kolom (BU/Periode/Jenis/Status/Tombol)
                  SELALU sama lebar dan sejajar — flex-wrap sebelumnya memutus tombol
                  Buka/Refresh ke baris sendiri karena lebar w-1/4 + gap tidak pernah pas
                  muat 4 kolom sekaligus dalam satu baris. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-start w-full px-4 py-3">
                <div>
                  <HrdInputGenerator
                    type={"BisnisUnit"}
                    name={"BisnisUnitID"}
                    title={"Bisnis Unit"}
                    value={values}
                    required={true}
                    onChange={customHandler}
                    touched={touched}
                    errors={errors}
                    size={"full"}
                    disabled={lockBU}
                    includeAll={!lockBU}
                  />
                  {isSO && (
                    <p className="mt-1 text-xs italic text-gray-500">
                      Terkunci ke unit Anda (SO).
                    </p>
                  )}
                </div>
                <div>
                  <HrdInputGenerator
                    type={"bulan"}
                    name={"Bulan"}
                    title={"Periode"}
                    value={values}
                    required={true}
                    onChange={customHandler}
                    touched={touched}
                    errors={errors}
                    size={"full"}
                  />
                </div>
                <div>
                  {/* Pembungkus & kelas kontrol sengaja MENIRU HrdInputGenerator
                      (size="full" membungkus dirinya dgn "w-full px-3 mb-6") supaya
                      kolom ini sebaris rapi dgn Bisnis Unit dan Periode. */}
                  <div className="w-full px-3 mb-6">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Jenis Transaksi
                    </label>
                    <select
                      value={jenisFilter}
                      onChange={(e) => setJenisFilter(e.target.value)}
                      className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                    >
                      <option value="">Semua Jenis Transaksi</option>
                      {jenisOptions.map(([jenis, jml]) => (
                        <option key={jenis} value={jenis}>
                          {jenis} ({jml})
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs italic text-gray-500">
                      Berlaku langsung, tanpa menekan Buka.
                    </p>
                  </div>
                </div>
                <div>
                  <div className="w-full px-3 mb-6">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="appearance-none block w-full text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight outline-none bg-white border-gray-500"
                    >
                      <option value="">Semua Status</option>
                      {statusOptions.map(([status, jml]) => (
                        <option key={status} value={status}>
                          {status} ({jml})
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs italic text-gray-500">
                      Berlaku langsung, tanpa menekan Buka.
                    </p>
                  </div>
                </div>
                <div>
                  <div className="w-full px-3 mb-6">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      &nbsp;
                    </label>
                    <div className="flex flex-row gap-2">
                      <button
                        type="button"
                        onClick={filterApi}
                        className="h-[50px] flex-1 text-main-100 hover:text-white border border-main-100 hover:bg-main-100 font-medium rounded-lg text-sm px-4 text-center"
                      >
                        Buka
                      </button>
                      <button
                        type="button"
                        onClick={handleRefresh}
                        className="h-[50px] flex-1 text-gray-600 hover:text-white border border-gray-400 hover:bg-gray-400 font-medium rounded-lg text-sm px-4 text-center"
                      >
                        Refresh
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {mutationData.isLoading ? (
                <div className="w-full pt-0">
                  <Skeltons />
                </div>
              ) : (
                <>
                  {/* ── Toolbar: Export / Search ── */}
                  <div className="flex w-full justify-between px-4 pt-2 flex-col sm:flex-row mt-2 gap-2">
                    <div className="flex flex-row gap-2 flex-wrap">
                      <button
                        onClick={handleDownloadExcel}
                        className="flex items-center justify-center my-1 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-green-600 hover:bg-green-600 hover:text-white rounded shadow"
                      >
                        Download Excel
                      </button>
                      <button
                        onClick={handleDownloadPDF}
                        className="flex items-center justify-center my-1 sm:my-0 bg-white text-gray-800 font-semibold py-2 px-4 border border-red-400 hover:bg-red-400 hover:text-white rounded shadow"
                      >
                        Download PDF
                      </button>
                    </div>
                    <div className="flex flex-row w-96">
                      <SearchBarSmallV2
                        text={"Cari ID / Bagian / Kepada / Keterangan / Status"}
                        value={searchInput}
                        funcs={handleSearchChange}
                      />
                    </div>
                  </div>

                  {/* ── Tabel ── */}
                  <div className="overflow-x-auto px-4 pt-2">
                    <table className="table-auto w-full text-sm text-left text-gray-500 mt-4">
                      <thead className="text-xs text-orange-600 uppercase bg-gray-50">
                        <tr>
                          {colHeader.map((h, i) => (
                            <th key={i} scope="col" className="py-3 px-6">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={colHeader.length}
                              className="text-center py-8 text-gray-400"
                            >
                              {searchQuery
                                ? `Tidak ada data untuk "${searchQuery}"${
                                    jenisFilter ? ` pada jenis "${jenisFilter}"` : ""
                                  }${statusFilter ? ` berstatus "${statusFilter}"` : ""}`
                                : jenisFilter || statusFilter
                                ? `Tidak ada data${
                                    jenisFilter ? ` berjenis "${jenisFilter}"` : ""
                                  }${
                                    statusFilter ? ` berstatus "${statusFilter}"` : ""
                                  } pada periode ini`
                                : "Tidak ada data"}
                            </td>
                          </tr>
                        ) : (
                          pageRows.map((row, id) => {
                            const st = rowStatus(row);
                            const hasCompleteData =
                              (row.NoCek && row.NoCek !== "0") ||
                              row.PurchaseOrderID;
                            return (
                              <tr
                                key={row.CashJournalID || id}
                                className={
                                  id % 2 === 0
                                    ? "bg-gray-50 border-b"
                                    : "bg-white border-b"
                                }
                              >
                                <td className="py-4 px-6 whitespace-nowrap">
                                  {(safePage - 1) * PAGE_SIZE + id + 1}
                                </td>
                                <td className="py-4 px-6 whitespace-nowrap font-mono text-xs">
                                  {row.CashJournalID}
                                </td>
                                {isAllBU && (
                                  <td className="py-4 px-6 whitespace-nowrap">
                                    {row.NamaBisnisUnit || row.BisnisUnitID || "-"}
                                  </td>
                                )}
                                {/* Backend mengirim NAMA bagian di field BagianID
                                    (COALESCE MBagian.NamaBagian, 'Tanpa Bagian') —
                                    sama seperti yang sudah dipakai MonitoringHO. */}
                                <td className="py-4 px-6">{row.BagianID || "-"}</td>
                                <td className="py-4 px-6">{row.DariKepada || "-"}</td>
                                <td className="py-4 px-6">{row.JenisTransaksi || "-"}</td>
                                <td className="py-4 px-6 whitespace-nowrap">
                                  {row.NoCek && row.NoCek !== "0" ? row.NoCek : "-"}
                                </td>
                                <td className="py-4 px-6 whitespace-nowrap">
                                  {fmtTgl(row.Tanggal)}
                                </td>
                                <td className="py-4 px-6">{row.Keterangan || "-"}</td>
                                <td className="py-4 px-6">{row.JenisCashJournal || "-"}</td>
                                <td className="py-4 px-6 text-right whitespace-nowrap">
                                  {fmtRp(row.Nominal)}
                                </td>
                                <td className="py-4 px-6 whitespace-nowrap align-top">
                                  {st.isRejected ? (
                                    <button
                                      type="button"
                                      title="Lihat alasan penolakan"
                                      onClick={() => setRejectInfo(row)}
                                      className={`${statusClass(st)} underline decoration-dotted underline-offset-2 hover:opacity-80`}
                                    >
                                      {st.value}
                                    </button>
                                  ) : st.isApproved && row.TanggalBayar ? (
                                    /* "Terbayar" bukan angka status tersendiri,
                                       melainkan turunan dari TanggalBayar — sejak
                                       jurnal terbit saat pembayaran, kolom itulah
                                       penanda uang sudah benar-benar bergerak. */
                                    <span className="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-blue-100 text-blue-700">
                                      Terbayar {fmtTgl(row.TanggalBayar)}
                                    </span>
                                  ) : (
                                    <span className={statusClass(st)}>{st.value}</span>
                                  )}
                                  {st.isApproved && row.TanggalBayar && row.BuktiBayar && (
                                    <button
                                      type="button"
                                      onClick={() => bukaBukti(row.BuktiBayar)}
                                      className="block mt-1 text-xs text-blue-600 underline"
                                      title={row.BuktiBayar}
                                    >
                                      Lihat bukti bayar
                                    </button>
                                  )}

                                  {/* Peta aktor approval: menjawab "prosesnya sudah
                                      sampai siapa" tanpa harus membuka detail. */}
                                  {Array.isArray(row.ApprovalList) &&
                                    row.ApprovalList.length > 0 && (
                                      <div className="mt-1.5 space-y-0.5 border-t border-gray-200 pt-1.5">
                                        {row.ApprovalList.map((a, i) => (
                                          <div
                                            key={i}
                                            title={a.Keterangan || ""}
                                            className={`text-[11px] leading-tight ${aktorClass(a.Status)}`}
                                          >
                                            <span className="mr-1">{a.Status}</span>
                                            {a.Nama}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                </td>
                                {/* Hanya terisi untuk uang muka yang sudah Close —
                                    tanggal PembayaranAP disetujui yang memotongnya. */}
                                <td className="py-4 px-6 whitespace-nowrap align-top">
                                  {row.TanggalClose ? (
                                    <span className="text-blue-600 font-medium">
                                      {fmtTgl(row.TanggalClose)}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="py-4 px-6 whitespace-nowrap">
                                  {/* Bayar hanya untuk yang sudah disetujui tapi
                                      belum dibayar. Di sinilah jurnalnya terbit. */}
                                  {st.isApproved && !row.TanggalBayar && (
                                    <button
                                      onClick={() =>
                                        setBayarModal({
                                          show: true,
                                          id: row.CashJournalID,
                                          nominal: row.Nominal,
                                          tanggalDokumen: row.Tanggal,
                                        })
                                      }
                                      className="border text-white bg-blue-600 hover:bg-white hover:border-blue-600 hover:text-blue-600 font-medium rounded-lg text-sm px-4 py-2 mr-2"
                                      title="Catat pembayaran — jurnal terbit pada tanggal yang dipilih"
                                    >
                                      Bayar
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleViewDetail(row.CashJournalID)}
                                    className="border text-white bg-main-100 hover:bg-white hover:border-main-100 hover:text-main-100 font-medium rounded-lg text-sm px-4 py-2 mr-2"
                                    title="Lihat Detail"
                                  >
                                    Lihat
                                  </button>
                                  {(st.isApproved || hasCompleteData) && (
                                    <button
                                      onClick={() => handleViewBlanko(row.CashJournalID)}
                                      className="border text-white bg-green-600 hover:bg-white hover:border-green-600 hover:text-green-600 font-medium rounded-lg text-sm px-4 py-2"
                                      title="Cetak Blanko"
                                    >
                                      Blanko
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* ── Info + Pagination ── */}
                  <div className="flex flex-row flex-wrap justify-between items-center gap-4 px-4 py-4 text-sm text-gray-600">
                    <span>
                      {filtered.length} data — periode {periodeLabel} | BU:{" "}
                      {buLabel} | Total {fmtRp(grandTotal)}
                      {jenisFilter && ` — jenis "${jenisFilter}"`}
                      {statusFilter && ` — status "${statusFilter}"`}
                      {searchQuery && ` — hasil "${searchQuery}"`}
                    </span>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={safePage <= 1}
                        className="border px-4 py-1 rounded disabled:opacity-50 hover:bg-gray-100"
                      >
                        Prev
                      </button>
                      <span>
                        Page {safePage} / {totalPage}
                      </span>
                      <button
                        onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
                        disabled={safePage >= totalPage}
                        className="border px-4 py-1 rounded disabled:opacity-50 hover:bg-gray-100"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* ── Popup alasan penolakan ── */}
      {rejectInfo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setRejectInfo(null)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b px-5 py-3">
              <div>
                <h3 className="text-base font-semibold text-gray-800">
                  Pengajuan Ditolak
                </h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  {rejectInfo.CashJournalID}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setRejectInfo(null)}
                className="-mr-1 px-2 text-xl leading-none text-gray-400 hover:text-gray-600"
                aria-label="Tutup"
              >
                &times;
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Alasan Penolakan
                </div>
                {rejectInfo.KeteranganApproval ? (
                  <p className="mt-1 whitespace-pre-wrap rounded-md bg-red-50 p-3 text-sm text-red-900">
                    {rejectInfo.KeteranganApproval}
                  </p>
                ) : (
                  <p className="mt-1 text-sm italic text-gray-400">
                    Alasan tidak dicatat.
                  </p>
                )}
              </div>

              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Ditolak Oleh
                </div>
                {rejectInfo.UpdateBy ? (
                  <p className="mt-1 text-sm font-medium text-gray-800">
                    {rejectInfo.UpdateBy}
                  </p>
                ) : (
                  <p className="mt-1 text-sm italic text-gray-400">
                    Tidak tercatat — dokumen ini ditolak sebelum sistem mulai
                    mencatat aktor penolakan.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end border-t px-5 py-3">
              <Button
                size="sm"
                onClick={() => setRejectInfo(null)}
                style={{ background: Colors.primary }}
              >
                Tutup
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringNonPO;
